import Api from '@/helpers/Api';

/**
 * Shared by the extension landing page (./index.js) and the Download Plato File
 * admin screen (@/module/plato-file-download), which both turn a link into an
 * S3 object key and ask the BE for a signed URL.
 */

// Relative to REACT_APP_API_ROOT, which already ends in /api/.
export const DOWNLOAD_URL_ENDPOINT = 'files/download-url';

// Only mincdata S3 links are accepted in the ?fileUrl= form, so the page cannot
// be used to hand the BE an arbitrary URL. Mirrors the extension.
export const MINC_DATA_PATTERN = /minc[\w-]*\.s3[.-]/i;

/**
 * A space inside an S3 key reaches us as "+" (prod links) or "%20" (non-prod).
 * Normalise both to a real space so the key we send matches the object.
 */
export const decodeS3Key = (raw) => {
  try {
    return decodeURIComponent(raw.replace(/\+/g, '%20'));
  } catch (e) {
    return raw;
  }
};

/** Full S3 URL -> object key ("https://bucket.s3.../A/b.pdf" -> "A/b.pdf") */
export const keyFromFileUrl = (fileUrl) => {
  try {
    return decodeS3Key(new URL(fileUrl).pathname.replace(/^\//, ''));
  } catch (e) {
    return '';
  }
};

/**
 * Whatever someone pastes -> object key. Three shapes are accepted, because the
 * link reaching a staff member could be any of them:
 *   1. the BE download endpoint itself, carrying the key as ?FileName=
 *   2. a direct S3 URL
 *   3. a bare object key
 * Returns '' when the input cannot yield a usable key.
 */
export const keyFromAnyInput = (raw) => {
  const input = (raw || '').trim();
  if (!input) return '';

  let key;
  try {
    // URLSearchParams decodes both "%20" and "+" to a real space already, so
    // the FileName branch must not go through decodeS3Key a second time.
    const fileName = new URL(input).searchParams.get('FileName');
    key = fileName ? fileName.trim() : keyFromFileUrl(input);
  } catch (e) {
    key = decodeS3Key(input);
  }

  // Reject traversal, the same guard the landing page applies.
  if (!key || key.split('/').includes('..')) return '';
  return key.replace(/^\//, '');
};

/**
 * The BE expects FileName with its slashes intact (FileName=Staging/A%20B/x.pdf),
 * and Api.createUrl() interpolates params without encoding them at all - so the
 * query string is built here instead of being passed through `params`.
 */
export const fetchSignedUrl = async (fileName) => {
  const fileNameParam = encodeURIComponent(fileName).replace(/%2F/g, '/');
  const { result, status } = await Api.get(
    `${DOWNLOAD_URL_ENDPOINT}?FileName=${fileNameParam}`,
    undefined,
    undefined,
    false
  );
  if (status !== 200) return null;
  return typeof result === 'string' ? result : result?.url;
};
